import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Search, Phone, Clock, Calendar, MessageSquare, TrendingUp, TrendingDown,
  Minus, ChevronLeft, ChevronRight, Trash2, Bot, Globe, Mic, Users, FileText, RefreshCw,
  CheckCircle2, XCircle
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { apiClient, type ApiResponse } from '@/lib/api';

type SourceType = 'openai' | 'elevenlabs' | 'chatbot' | 'gemini-voice';

interface Transcription {
  id: string;
  agent_id: string;
  agent_name: string;
  call_id: string;
  caller_id: string;
  callee_id: string;
  trunk_id: string;
  transcription: string;
  summary: string;
  duration_seconds: number;
  sentiment_score: number;
  sentiment_label: 'positive' | 'neutral' | 'negative';
  sentiment_moments: Array<{ timestamp: string; sentiment: string; text: string }>;
  created_at: string;
}

interface OpenAIStats {
  total_calls: number;
  avg_duration: number;
  avg_sentiment: number;
  completed_count: number;
  failed_count: number;
  positive_count: number;
  neutral_count: number;
  negative_count: number;
}

interface TranscriptTurn {
  role?: string;
  speaker?: string;
  message?: string;
  text?: string;
  content?: string;
  timestamp?: string;
}

interface ElevenLabsConversation {
  id: string;
  elevenlabs_conversation_id: string;
  agent_id: string;
  agent_name: string;
  status: string;
  duration_seconds: number;
  transcript: TranscriptTurn[] | { input?: string[]; output?: string[] } | string | null;
  analysis: Record<string, unknown> | string | null;
  detected_language: string;
  overall_rating: number;
  caller_number: string;
  channel: string;
  has_audio: boolean;
  metadata: Record<string, unknown> | null;
  created_at: string;
  sentiment_label?: string;
  sentiment_score?: number;
  summary?: string;
}

interface ElevenLabsStats {
  total_conversations: number;
  avg_duration: number;
  total_duration: number;
  completed_count: number;
  failed_count: number;
  positive_count: number;
  negative_count: number;
}

interface ChatMessage {
  role?: string;
  content?: string;
  text?: string;
  message?: string;
  timestamp?: string;
}

interface ChatbotConversation {
  id: string;
  chatbot_agent_id: string;
  agent_name: string;
  visitor_id: string;
  messages: ChatMessage[];
  summary: string;
  lead_id: string;
  lead_title: string;
  metadata: Record<string, unknown> | null;
  started_at: string;
  ended_at: string;
  created_at: string;
  sentiment_label?: string;
  sentiment_score?: number;
}

interface GeminiVoiceSession {
  id: string;
  agent_id?: string;
  agent_name?: string;
  caller_number?: string;
  status?: string;
  channel?: string;
  duration_seconds?: number;
  started_at?: string;
  ended_at?: string;
  summary?: string;
  model?: string;
  voice?: string;
  tokens_input?: number;
  tokens_output?: number;
  transcript?: TranscriptTurn[] | { input?: string[]; output?: string[] } | null;
  tool_calls?: Array<{ name?: string; function_name?: string; args?: unknown }>;
  sentiment_label?: string;
  sentiment_score?: number;
}

interface GeminiVoiceStats {
  total_sessions?: number;
  avg_duration?: number;
  completed_count?: number;
  error_count?: number;
  positive_count?: number;
  negative_count?: number;
}

type SelectedDetailItem =
  | (Transcription & { _source: 'openai'; sentiment_moments?: Array<{ timestamp: string; sentiment: string; text: string }> })
  | (ElevenLabsConversation & { _source: 'elevenlabs' })
  | (GeminiVoiceSession & { _source: 'gemini-voice' })
  | (ChatbotConversation & { _source: 'chatbot' });

interface ChatbotStats {
  total_conversations: number;
  leads_generated: number;
  with_summary: number;
}

interface Agent {
  id: string;
  name: string;
}

const SOURCE_CONFIG = {
  openai: {
    label: 'Agenti AI',
    color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
    activeColor: 'bg-green-600 text-white',
    icon: Phone,
    accent: 'text-green-600 dark:text-green-400',
    border: 'border-l-green-500',
  },
  elevenlabs: {
    label: 'ElevenLabs',
    color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700',
    activeColor: 'bg-cyan-600 text-white',
    icon: Mic,
    accent: 'text-cyan-600 dark:text-cyan-400',
    border: 'border-l-cyan-500',
  },
  chatbot: {
    label: 'Chatbot AI',
    color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
    activeColor: 'bg-purple-600 text-white',
    icon: Bot,
    accent: 'text-purple-600 dark:text-purple-400',
    border: 'border-l-purple-500',
  },
  'gemini-voice': {
    label: 'Gemini Voice',
    color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700',
    activeColor: 'bg-blue-600 text-white',
    icon: Mic,
    accent: 'text-blue-600 dark:text-blue-400',
    border: 'border-l-blue-500',
  },
};

export default function TranscriptionsDashboard() {
  const [source, setSource] = useState<SourceType>('openai');

  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [openaiStats, setOpenaiStats] = useState<OpenAIStats | null>(null);

  const [elConversations, setElConversations] = useState<ElevenLabsConversation[]>([]);
  const [elStats, setElStats] = useState<ElevenLabsStats | null>(null);

  const [chatConversations, setChatConversations] = useState<ChatbotConversation[]>([]);
  const [chatStats, setChatStats] = useState<ChatbotStats | null>(null);

  const [gvSessions, setGvSessions] = useState<GeminiVoiceSession[]>([]);
  const [gvStats, setGvStats] = useState<GeminiVoiceStats | null>(null);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<SelectedDetailItem | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [sentimentFilter, setSentimentFilter] = useState<string>('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setAgentFilter('all');
    setSentimentFilter('all');
    setSearchFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  }, [source]);

  const loadAgents = useCallback(async () => {
    try {
      if (source === 'openai') {
        const res = await apiClient.get<Agent[]>('/ai/voice-agents');
        setAgents(res.data || []);
      } else if (source === 'elevenlabs') {
        const res = await apiClient.get<Array<{ id: string; name: string }>>('/elevenlabs/agents');
        const elAgents = (res.data || []).map(a => ({ id: a.id, name: a.name }));
        setAgents(elAgents);
      } else if (source === 'gemini-voice') {
        const res = await apiClient.get<Array<{ id: string; name: string }>>('/gemini-voice/agents');
        const gvAgents = (res.data || []).map(a => ({ id: a.id, name: a.name }));
        setAgents(gvAgents);
      } else {
        const res = await apiClient.get<Array<{ id: string; name?: string; agent_name?: string }>>('/chatbot/agents');
        const cbAgents = (res.data || []).map(a => ({ id: a.id, name: a.name || a.agent_name || '' }));
        setAgents(cbAgents);
      }
    } catch (error) {
      console.error('Error loading agents:', error);
      setAgents([]);
    }
  }, [source]);

  useEffect(() => {
    loadAgents();
  }, [source, loadAgents]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (source === 'openai') {
        let endpoint = `/ai/transcriptions?page=${page}&limit=50`;
        if (agentFilter !== 'all') endpoint += `&agent_id=${agentFilter}`;
        if (sentimentFilter !== 'all') endpoint += `&sentiment=${sentimentFilter}`;
        if (searchFilter) endpoint += `&caller_id=${encodeURIComponent(searchFilter)}`;
        if (dateFrom) endpoint += `&start_date=${dateFrom}`;
        if (dateTo) endpoint += `&end_date=${dateTo}`;
        const res = await apiClient.get<Transcription[]>(endpoint);
        if (res.success) {
          setTranscriptions(res.data || []);
          const pag = res.pagination as ({ pages?: number; totalPages?: number } | undefined);
          setTotalPages(pag?.pages ?? pag?.totalPages ?? 1);
        } else {
          setTranscriptions([]);
        }
      } else if (source === 'elevenlabs') {
        let endpoint = `/elevenlabs/transcriptions?page=${page}&limit=50`;
        if (agentFilter !== 'all') endpoint += `&agent_id=${agentFilter}`;
        if (searchFilter) endpoint += `&caller_id=${encodeURIComponent(searchFilter)}`;
        if (dateFrom) endpoint += `&start_date=${dateFrom}`;
        if (dateTo) endpoint += `&end_date=${dateTo}`;
        const res = await apiClient.get<ElevenLabsConversation[]>(endpoint);
        if (res.success) {
          setElConversations(res.data || []);
          const pag = res.pagination as ({ pages?: number; totalPages?: number } | undefined);
          setTotalPages(pag?.pages ?? pag?.totalPages ?? 1);
        } else {
          setElConversations([]);
        }
      } else if (source === 'gemini-voice') {
        let endpoint = `/gemini-voice/sessions?page=${page}&limit=20`;
        if (agentFilter !== 'all') endpoint += `&agent_id=${agentFilter}`;
        if (dateFrom) endpoint += `&from=${dateFrom}`;
        if (dateTo) endpoint += `&to=${dateTo}`;
        const res = await apiClient.get<GeminiVoiceSession[]>(endpoint);
        const total = (res as ApiResponse<GeminiVoiceSession[]> & { total?: number }).total;
        if (res.success) {
          setGvSessions(res.data || []);
          setTotalPages(Math.ceil((total || 0) / 20));
        } else {
          setGvSessions([]);
        }
      } else {
        let endpoint = `/chatbot/transcriptions?page=${page}&limit=50`;
        if (agentFilter !== 'all') endpoint += `&agent_id=${agentFilter}`;
        if (searchFilter) endpoint += `&visitor_id=${encodeURIComponent(searchFilter)}`;
        if (dateFrom) endpoint += `&start_date=${dateFrom}`;
        if (dateTo) endpoint += `&end_date=${dateTo}`;
        const res = await apiClient.get<ChatbotConversation[]>(endpoint);
        if (res.success) {
          setChatConversations(res.data || []);
          const pag = res.pagination as ({ pages?: number; totalPages?: number } | undefined);
          setTotalPages(pag?.pages ?? pag?.totalPages ?? 1);
        } else {
          setChatConversations([]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [agentFilter, dateFrom, dateTo, page, searchFilter, sentimentFilter, source]);

  const loadStats = useCallback(async () => {
    try {
      if (source === 'openai') {
        let endpoint = '/ai/transcriptions-stats';
        if (agentFilter !== 'all') endpoint += `?agent_id=${agentFilter}`;
        const res = await apiClient.get<OpenAIStats>(endpoint);
        if (res.success) setOpenaiStats(res.data || null);
      } else if (source === 'elevenlabs') {
        let endpoint = '/elevenlabs/transcriptions-stats';
        if (agentFilter !== 'all') endpoint += `?agent_id=${agentFilter}`;
        const res = await apiClient.get<ElevenLabsStats>(endpoint);
        if (res.success) setElStats(res.data || null);
      } else if (source === 'gemini-voice') {
        let endpoint = '/gemini-voice/sessions/stats';
        const qp: string[] = [];
        if (dateFrom) qp.push(`from=${dateFrom}`);
        if (dateTo) qp.push(`to=${dateTo}`);
        if (qp.length > 0) endpoint += '?' + qp.join('&');
        const res = await apiClient.get<GeminiVoiceStats>(endpoint);
        if (res.success) setGvStats(res.data || null);
      } else {
        let endpoint = '/chatbot/transcriptions-stats';
        if (agentFilter !== 'all') endpoint += `?agent_id=${agentFilter}`;
        const res = await apiClient.get<ChatbotStats>(endpoint);
        if (res.success) setChatStats(res.data || null);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [agentFilter, dateFrom, dateTo, source]);

  useEffect(() => {
    loadData();
    loadStats();
  }, [source, agentFilter, sentimentFilter, searchFilter, dateFrom, dateTo, page, loadData, loadStats]);

  useEffect(() => {
    if (source === 'elevenlabs') {
      const doAutoSync = async () => {
        try {
          const res = await apiClient.post<{ synced?: number }>('/elevenlabs/sync-conversations');
          const synced = res.data?.synced || 0;
          if (synced > 0) {
            setSyncMessage(`Sincronizzate ${synced} nuove conversazioni`);
            setTimeout(() => setSyncMessage(null), 4000);
            loadData();
            loadStats();
          }
        } catch (err) {
          console.error('Auto-sync failed:', err);
        }
      };
      doAutoSync();
    }
  }, [source, loadData, loadStats]);

  const syncElevenLabsConversations = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await apiClient.post<{ synced?: number }>('/elevenlabs/sync-conversations');
      const synced = res.data?.synced || 0;
      setSyncMessage(`Sincronizzate ${synced} nuove conversazioni`);
      if (synced > 0) {
        loadData();
        loadStats();
      }
    } catch (error) {
      setSyncMessage('Errore durante la sincronizzazione');
      console.error('Error syncing conversations:', error);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  const openDetail = async (id: string) => {
    try {
      if (source === 'openai') {
        const res = await apiClient.get<Transcription & { sentiment_moments?: Array<{ timestamp: string; sentiment: string; text: string }> }>(`/ai/transcriptions/${id}`);
        if (res.success && res.data) {
          setSelectedItem({ ...res.data, _source: 'openai' });
          setDetailOpen(true);
        }
      } else if (source === 'elevenlabs') {
        const res = await apiClient.get<ElevenLabsConversation>(`/elevenlabs/transcriptions/${id}`);
        if (res.success && res.data) {
          setSelectedItem({ ...res.data, _source: 'elevenlabs' });
          setDetailOpen(true);
        }
      } else if (source === 'gemini-voice') {
        const res = await apiClient.get<GeminiVoiceSession>(`/gemini-voice/sessions/${id}`);
        if (res.success && res.data) {
          setSelectedItem({ ...res.data, _source: 'gemini-voice' });
          setDetailOpen(true);
        }
      } else {
        const res = await apiClient.get<ChatbotConversation>(`/chatbot/transcriptions/${id}`);
        if (res.success && res.data) {
          setSelectedItem({ ...res.data, _source: 'chatbot' });
          setDetailOpen(true);
        }
      }
    } catch (error) {
      console.error('Error loading detail:', error);
    }
  };

  const deleteTranscription = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm('Sei sicuro di voler eliminare questa trascrizione?')) return;
    try {
      const res = await apiClient.delete(`/ai/transcriptions/${id}`);
      if (res.success) {
        setTranscriptions(prev => prev.filter(t => t.id !== id));
        loadStats();
      }
    } catch (error) {
      console.error('Error deleting transcription:', error);
    }
  };

  const getSentimentBadge = (label: string, score?: number) => {
    const config = {
      positive: { color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300', icon: TrendingUp },
      neutral: { color: 'bg-muted text-muted-foreground', icon: Minus },
      negative: { color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300', icon: TrendingDown }
    };
    const { color, icon: Icon } = config[label as keyof typeof config] || config.neutral;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label} {score !== undefined && `(${(score * 100).toFixed(0)}%)`}
      </Badge>
    );
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMessageCount = (messages: unknown) => {
    if (Array.isArray(messages)) return messages.length;
    if (typeof messages === 'object' && messages !== null) {
      const arr = Object.values(messages);
      return arr.length;
    }
    return 0;
  };

  const getTranscriptText = (transcript: unknown): string => {
    if (!transcript) return '';
    if (typeof transcript === 'string') return transcript;
    if (Array.isArray(transcript)) {
      return (transcript as TranscriptTurn[]).map(t => `${t.role || t.speaker || 'Speaker'}: ${t.message || t.text || t.content || ''}`).join('\n');
    }
    const obj = transcript as { input?: unknown; output?: unknown };
    if (obj.input || obj.output) {
      const parts: string[] = [];
      const inputArr = Array.isArray(obj.input) ? obj.input as string[] : [];
      const outputArr = Array.isArray(obj.output) ? obj.output as string[] : [];
      outputArr.forEach((text, i) => {
        parts.push(`Agente: ${text}`);
        if (inputArr[i]) parts.push(`Utente: ${inputArr[i]}`);
      });
      if (inputArr.length > outputArr.length) {
        for (let i = outputArr.length; i < inputArr.length; i++) {
          parts.push(`Utente: ${inputArr[i]}`);
        }
      }
      return parts.join('\n');
    }
    return JSON.stringify(transcript);
  };

  const SourceIcon = SOURCE_CONFIG[source].icon;

  const renderSourceTabs = () => (
    <div className="flex gap-2 mb-6">
      {(Object.keys(SOURCE_CONFIG) as SourceType[]).map(s => {
        const cfg = SOURCE_CONFIG[s];
        const Icon = cfg.icon;
        const isActive = source === s;
        return (
          <button
            key={s}
            onClick={() => setSource(s)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
              isActive ? cfg.activeColor + ' border-transparent shadow-sm' : 'bg-card ' + cfg.color + ' hover:opacity-80'
            }`}
          >
            <Icon className="w-4 h-4" />
            {cfg.label}
          </button>
        );
      })}
    </div>
  );

  const renderUniformStats = (config: {
    totalLabel: string;
    totalIcon: LucideIcon;
    totalIconClass: string;
    total: number;
    avgDuration: number;
    completed: number;
    failedLabel: string;
    failed: number;
    positive: number;
    negative: number;
  }) => {
    const TotalIcon = config.totalIcon;
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{config.totalLabel}</p>
                <p className="text-2xl font-bold text-foreground">{config.total}</p>
              </div>
              <TotalIcon className={`w-8 h-8 ${config.totalIconClass}`} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Durata Media</p>
                <p className="text-2xl font-bold text-foreground">{formatDuration(Math.round(config.avgDuration))}</p>
              </div>
              <Clock className="w-8 h-8 text-purple-500 dark:text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completate</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{config.completed}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{config.failedLabel}</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{config.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sentiment Positivo</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{config.positive}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sentiment Negativo</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{config.negative}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderStats = () => {
    if (source === 'openai') {
      return renderUniformStats({
        totalLabel: 'Chiamate Totali',
        totalIcon: Phone,
        totalIconClass: 'text-green-500 dark:text-green-400',
        total: Number(openaiStats?.total_calls) || 0,
        avgDuration: Number(openaiStats?.avg_duration) || 0,
        completed: Number(openaiStats?.completed_count) || 0,
        failedLabel: 'Fallite',
        failed: Number(openaiStats?.failed_count) || 0,
        positive: Number(openaiStats?.positive_count) || 0,
        negative: Number(openaiStats?.negative_count) || 0,
      });
    }

    if (source === 'elevenlabs') {
      return renderUniformStats({
        totalLabel: 'Conversazioni',
        totalIcon: Mic,
        totalIconClass: 'text-cyan-500 dark:text-cyan-400',
        total: Number(elStats?.total_conversations) || 0,
        avgDuration: Number(elStats?.avg_duration) || 0,
        completed: Number(elStats?.completed_count) || 0,
        failedLabel: 'Fallite',
        failed: Number(elStats?.failed_count) || 0,
        positive: Number(elStats?.positive_count) || 0,
        negative: Number(elStats?.negative_count) || 0,
      });
    }

    if (source === 'gemini-voice') {
      return renderUniformStats({
        totalLabel: 'Sessioni Totali',
        totalIcon: Mic,
        totalIconClass: 'text-blue-500 dark:text-blue-400',
        total: Number(gvStats?.total_sessions) || 0,
        avgDuration: Number(gvStats?.avg_duration) || 0,
        completed: Number(gvStats?.completed_count) || 0,
        failedLabel: 'Errori',
        failed: Number(gvStats?.error_count) || 0,
        positive: Number(gvStats?.positive_count) || 0,
        negative: Number(gvStats?.negative_count) || 0,
      });
    }

    return (
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversazioni</p>
                <p className="text-2xl font-bold text-foreground">{chatStats?.total_conversations || 0}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-500 dark:text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Lead Generati</p>
                <p className="text-2xl font-bold text-foreground">{chatStats?.leads_generated || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500 dark:text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Con Riassunto</p>
                <p className="text-2xl font-bold text-foreground">{chatStats?.with_summary || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderFilters = () => (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex flex-wrap gap-4">
          <Select value={agentFilter} onValueChange={v => { setAgentFilter(v); setPage(1); }}>
            <SelectTrigger className="w-48 bg-muted border-border">
              <SelectValue placeholder="Tutti gli agenti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli agenti</SelectItem>
              {agents.map(a => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {source === 'openai' && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground font-medium">Sentiment:</span>
              <Select value={sentimentFilter} onValueChange={v => { setSentimentFilter(v); setPage(1); }}>
                <SelectTrigger className="w-36 bg-muted border-border">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="positive">
                    <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-500 dark:text-green-400" /> Positivo</span>
                  </SelectItem>
                  <SelectItem value="neutral">
                    <span className="flex items-center gap-1"><Minus className="w-3 h-3 text-muted-foreground" /> Neutro</span>
                  </SelectItem>
                  <SelectItem value="negative">
                    <span className="flex items-center gap-1"><TrendingDown className="w-3 h-3 text-red-500 dark:text-red-400" /> Negativo</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground font-medium">Data:</span>
            <Input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="w-36 bg-muted border-border"
              placeholder="Da"
            />
            <span className="text-muted-foreground">-</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="w-36 bg-muted border-border"
              placeholder="A"
            />
          </div>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={source === 'chatbot' ? 'Cerca visitatore...' : 'Cerca numero chiamante...'}
              value={searchFilter}
              onChange={e => { setSearchFilter(e.target.value); setPage(1); }}
              className="pl-10 bg-muted border-border"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderOpenAIList = () => (
    <div className="space-y-3">
      {transcriptions.map(t => (
        <div
          key={t.id}
          className="p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors border border-border border-l-4 border-l-green-500"
          onClick={() => openDetail(t.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="font-medium text-foreground">{t.caller_id}</span>
                <span className="text-muted-foreground">→</span>
                <span className="text-foreground">{t.agent_name || 'Agente'}</span>
                {t.sentiment_label && getSentimentBadge(t.sentiment_label, t.sentiment_score)}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 ml-7">
                {t.summary || t.transcription?.substring(0, 150) || 'Nessun contenuto'}...
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-right text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(t.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(t.duration_seconds || 0)}
                </div>
              </div>
              <button
                onClick={(e) => deleteTranscription(e, t.id)}
                className="p-1.5 text-muted-foreground hover:text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:bg-red-900/20 rounded transition-colors"
                title="Elimina trascrizione"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderElevenLabsList = () => (
    <div className="space-y-3">
      {elConversations.map(c => {
        const transcriptPreview = getTranscriptText(c.transcript).substring(0, 150);
        return (
          <div
            key={c.id}
            className="p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors border border-border border-l-4 border-l-cyan-500"
            onClick={() => openDetail(c.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Mic className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                  <span className="font-medium text-foreground">{c.caller_number || (c.channel === 'webrtc' ? 'WebRTC' : 'Chiamante sconosciuto')}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-foreground">{c.agent_name || 'Agente'}</span>
                  <Badge className={c.channel === 'sip' ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'}>
                    {c.channel === 'sip' ? 'SIP' : 'WebRTC'}
                  </Badge>
                  <Badge className={c.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : c.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-muted text-muted-foreground'}>
                    {c.status}
                  </Badge>
                  {c.sentiment_label && getSentimentBadge(c.sentiment_label, c.sentiment_score)}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 ml-7">
                  {transcriptPreview || 'Nessuna trascrizione disponibile'}...
                </p>
              </div>
              <div className="text-right text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(c.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(c.duration_seconds || 0)}
                </div>
                {c.detected_language && (
                  <div className="flex items-center gap-1 mt-1">
                    <Globe className="w-3 h-3" />
                    {c.detected_language}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderChatbotList = () => (
    <div className="space-y-3">
      {chatConversations.map(c => {
        const msgCount = getMessageCount(c.messages);
        return (
          <div
            key={c.id}
            className="p-4 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors border border-border border-l-4 border-l-purple-500"
            onClick={() => openDetail(c.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Bot className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="font-medium text-foreground">{c.visitor_id || 'Visitatore'}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-foreground">{c.agent_name || 'Chatbot'}</span>
                  <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                    {msgCount} messaggi
                  </Badge>
                  {c.lead_title && (
                    <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                      <Users className="w-3 h-3 mr-1" />Lead
                    </Badge>
                  )}
                  {c.sentiment_label && getSentimentBadge(c.sentiment_label, c.sentiment_score)}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 ml-7">
                  {c.summary || 'Nessun riassunto disponibile'}
                </p>
              </div>
              <div className="text-right text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(c.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                {c.started_at && c.ended_at && (
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDuration(Math.round((new Date(c.ended_at).getTime() - new Date(c.started_at).getTime()) / 1000))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderGeminiVoiceList = () => (
    <div className="space-y-3">
      {gvSessions.map(s => (
        <div
          key={s.id}
          className="p-4 bg-muted/50 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors border border-border border-l-4 border-l-blue-500"
          onClick={() => openDetail(s.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Mic className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="font-medium text-foreground">{s.caller_number || 'WebRTC'}</span>
                <span className="text-muted-foreground">→</span>
                <span className="text-foreground">{s.agent_name || 'Agente'}</span>
                <Badge className={`text-[10px] ${s.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : s.status === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'}`}>
                  {s.status}
                </Badge>
                <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px]">{s.channel}</Badge>
                {s.sentiment_label && getSentimentBadge(s.sentiment_label, s.sentiment_score)}
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 ml-7">
                {s.summary || 'Nessun riassunto disponibile'}
              </p>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {s.started_at && new Date(s.started_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3" />
                {formatDuration(s.duration_seconds || 0)}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderList = () => {
    const isEmpty = source === 'openai' ? transcriptions.length === 0 :
                    source === 'elevenlabs' ? elConversations.length === 0 :
                    source === 'gemini-voice' ? gvSessions.length === 0 :
                    chatConversations.length === 0;

    const listTitle = source === 'openai' ? 'Trascrizioni Chiamate' :
                      source === 'elevenlabs' ? 'Conversazioni ElevenLabs' :
                      source === 'gemini-voice' ? 'Sessioni Gemini Voice' :
                      'Conversazioni Chatbot';

    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-foreground">
              <SourceIcon className="w-5 h-5" />
              {listTitle}
            </CardTitle>
            {source === 'elevenlabs' && (
              <div className="flex items-center gap-2">
                {syncMessage && (
                  <span className="text-sm text-muted-foreground">{syncMessage}</span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={syncElevenLabsConversations}
                  disabled={syncing}
                  className="flex items-center gap-1.5"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Sincronizzando...' : 'Sincronizza da API'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : isEmpty ? (
            <div className="text-center py-8 text-muted-foreground">
              Nessun risultato trovato
            </div>
          ) : (
            <>
              {source === 'openai' && renderOpenAIList()}
              {source === 'elevenlabs' && renderElevenLabsList()}
              {source === 'gemini-voice' && renderGeminiVoiceList()}
              {source === 'chatbot' && renderChatbotList()}
            </>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">Pagina {page} di {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderOpenAIDetail = () => {
    const item = selectedItem as Transcription & { _source: string };
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Chiamante:</span>
            <p className="font-medium text-foreground">{item.caller_id}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Agente:</span>
            <p className="font-medium text-foreground">{item.agent_name}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Durata:</span>
            <p className="font-medium text-foreground">{formatDuration(item.duration_seconds || 0)}</p>
          </div>
        </div>
        {item.summary && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Riassunto</h4>
            <p className="text-sm bg-muted/50 p-3 rounded border border-border text-foreground">{item.summary}</p>
          </div>
        )}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Trascrizione Completa</h4>
          <div className="text-sm bg-muted/50 p-4 rounded border border-border max-h-64 overflow-y-auto whitespace-pre-wrap text-foreground">
            {item.transcription || 'Nessuna trascrizione disponibile'}
          </div>
        </div>
        {item.sentiment_moments && item.sentiment_moments.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Momenti Chiave</h4>
            <div className="space-y-2">
              {item.sentiment_moments.map((m, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className="text-muted-foreground w-12">{m.timestamp}</span>
                  <Badge className={
                    m.sentiment === 'positive' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                    m.sentiment === 'negative' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                    'bg-muted text-muted-foreground'
                  }>
                    {m.sentiment}
                  </Badge>
                  <span className="flex-1 text-foreground">{m.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderElevenLabsDetail = () => {
    const item = selectedItem as ElevenLabsConversation & { _source: string };
    const transcriptArr: TranscriptTurn[] = Array.isArray(item.transcript) ? item.transcript : [];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Chiamante:</span>
            <p className="font-medium text-foreground">{item.caller_number || 'N/D'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Agente:</span>
            <p className="font-medium text-foreground">{item.agent_name || 'N/D'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Durata:</span>
            <p className="font-medium text-foreground">{formatDuration(item.duration_seconds || 0)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Canale:</span>
            <Badge className={item.channel === 'sip' ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' : 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'}>
              {item.channel === 'sip' ? 'SIP' : 'WebRTC'}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Badge className={item.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}>
            {item.status}
          </Badge>
          {item.detected_language && (
            <Badge className="bg-muted text-foreground">
              <Globe className="w-3 h-3 mr-1" />{item.detected_language}
            </Badge>
          )}
          {item.overall_rating != null && (
            <Badge className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
              ★ {Number(item.overall_rating).toFixed(1)}
            </Badge>
          )}
          {item.sentiment_label && getSentimentBadge(item.sentiment_label, item.sentiment_score)}
        </div>

        {item.summary && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Riepilogo</h4>
            <p className="text-sm bg-muted/50 p-3 rounded border border-border text-foreground">{item.summary}</p>
          </div>
        )}

        {item.analysis && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Analisi</h4>
            <div className="text-sm bg-muted/50 p-3 rounded border border-border text-foreground">
              {typeof item.analysis === 'string' ? item.analysis : (
                <div className="space-y-1">
                  {Object.entries(item.analysis).map(([key, val]) => (
                    <div key={key}>
                      <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                      <span>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Trascrizione</h4>
          <div className="bg-muted/50 rounded border border-border max-h-72 overflow-y-auto p-4 space-y-3">
            {transcriptArr.length > 0 ? transcriptArr.map((turn, i) => {
              const isAgent = (turn.role || turn.speaker || '').toLowerCase().includes('agent') ||
                              (turn.role || turn.speaker || '').toLowerCase().includes('assistant');
              return (
                <div key={i} className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                    isAgent ? 'bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 text-foreground' : 'bg-card border border-border text-foreground'
                  }`}>
                    <span className={`text-xs font-medium block mb-1 ${isAgent ? 'text-cyan-600 dark:text-cyan-400' : 'text-muted-foreground'}`}>
                      {turn.role || turn.speaker || (isAgent ? 'Agente' : 'Utente')}
                    </span>
                    {turn.message || turn.text || turn.content || ''}
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{getTranscriptText(item.transcript) || 'Nessuna trascrizione disponibile'}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGeminiVoiceDetail = () => {
    const item = selectedItem as GeminiVoiceSession & { _source: string };
    const transcriptArr: TranscriptTurn[] = [];
    if (Array.isArray(item.transcript)) {
      transcriptArr.push(...item.transcript);
    } else if (item.transcript && typeof item.transcript === 'object') {
      const tObj = item.transcript as { input?: unknown; output?: unknown };
      const inputArr: string[] = Array.isArray(tObj.input) ? (tObj.input as string[]) : [];
      const outputArr: string[] = Array.isArray(tObj.output) ? (tObj.output as string[]) : [];
      outputArr.forEach((text, i) => {
        transcriptArr.push({ role: 'assistant', text });
        if (inputArr[i]) {
          transcriptArr.push({ role: 'user', text: inputArr[i] });
        }
      });
      if (inputArr.length > outputArr.length) {
        for (let i = outputArr.length; i < inputArr.length; i++) {
          transcriptArr.push({ role: 'user', text: inputArr[i] });
        }
      }
    }
    const toolCalls = Array.isArray(item.tool_calls) ? item.tool_calls : [];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Chiamante:</span>
            <p className="font-medium text-foreground">{item.caller_number || 'WebRTC'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Agente:</span>
            <p className="font-medium text-foreground">{item.agent_name || 'N/D'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Durata:</span>
            <p className="font-medium text-foreground">{formatDuration(item.duration_seconds || 0)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Canale:</span>
            <Badge className={item.channel === 'sip' ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}>
              {item.channel === 'sip' ? 'SIP' : 'WebRTC'}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Badge className={item.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : item.status === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'}>
            {item.status}
          </Badge>
          {item.model && (
            <Badge className="bg-muted text-foreground text-[10px]">{item.model}</Badge>
          )}
          {item.voice && (
            <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px]">{item.voice}</Badge>
          )}
          {item.sentiment_label && getSentimentBadge(item.sentiment_label, item.sentiment_score)}
        </div>

        {item.summary && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Riassunto</h4>
            <div className="text-sm bg-muted/50 p-3 rounded border border-border text-foreground">
              {item.summary}
            </div>
          </div>
        )}

        {(item.tokens_input || item.tokens_output) && (
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>Token input: <strong>{item.tokens_input || 0}</strong></span>
            <span>Token output: <strong>{item.tokens_output || 0}</strong></span>
          </div>
        )}

        {toolCalls.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Tool Calls ({toolCalls.length})</h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {toolCalls.map((tc, i) => (
                <div key={i} className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded p-2">
                  <span className="font-medium text-amber-700 dark:text-amber-300">{tc.name || tc.function_name || 'tool'}</span>
                  {tc.args != null && <span className="text-muted-foreground ml-2">{typeof tc.args === 'string' ? tc.args : JSON.stringify(tc.args)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Trascrizione</h4>
          <div className="bg-muted/50 rounded border border-border max-h-72 overflow-y-auto p-4 space-y-3">
            {transcriptArr.length > 0 ? transcriptArr.map((turn, i) => {
              const isAgent = (turn.role || turn.speaker || '').toLowerCase().includes('agent') ||
                              (turn.role || turn.speaker || '').toLowerCase().includes('assistant') ||
                              (turn.role || turn.speaker || '').toLowerCase().includes('model');
              return (
                <div key={i} className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                    isAgent ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-foreground' : 'bg-card border border-border text-foreground'
                  }`}>
                    <span className={`text-xs font-medium block mb-1 ${isAgent ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground'}`}>
                      {turn.role || turn.speaker || (isAgent ? 'Agente' : 'Utente')}
                    </span>
                    {turn.message || turn.text || turn.content || ''}
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-muted-foreground">Nessuna trascrizione disponibile</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderChatbotDetail = () => {
    const item = selectedItem as ChatbotConversation & { _source: string };
    const messages = Array.isArray(item.messages) ? item.messages : [];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Visitatore:</span>
            <p className="font-medium text-foreground">{item.visitor_id || 'Anonimo'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Agente:</span>
            <p className="font-medium text-foreground">{item.agent_name || 'Chatbot'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Messaggi:</span>
            <p className="font-medium text-foreground">{messages.length}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {item.lead_title && (
            <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              <Users className="w-3 h-3 mr-1" />Lead: {item.lead_title}
            </Badge>
          )}
          {item.started_at && item.ended_at && (
            <Badge className="bg-muted text-muted-foreground">
              <Clock className="w-3 h-3 mr-1" />
              {formatDuration(Math.round((new Date(item.ended_at).getTime() - new Date(item.started_at).getTime()) / 1000))}
            </Badge>
          )}
        </div>

        {item.summary && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Riassunto</h4>
            <p className="text-sm bg-muted/50 p-3 rounded border border-border text-foreground">{item.summary}</p>
          </div>
        )}

        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Conversazione</h4>
          <div className="bg-muted/50 rounded border border-border max-h-72 overflow-y-auto p-4 space-y-3">
            {messages.length > 0 ? messages.map((msg, i) => {
              const isBot = (msg.role || '').toLowerCase() === 'assistant' || (msg.role || '').toLowerCase() === 'bot';
              return (
                <div key={i} className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                    isBot ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 text-foreground' : 'bg-card border border-border text-foreground'
                  }`}>
                    <span className={`text-xs font-medium block mb-1 ${isBot ? 'text-purple-600 dark:text-purple-400' : 'text-muted-foreground'}`}>
                      {isBot ? 'Chatbot' : 'Visitatore'}
                    </span>
                    {msg.content || msg.text || msg.message || ''}
                  </div>
                </div>
              );
            }) : (
              <p className="text-sm text-muted-foreground">Nessun messaggio disponibile</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getDetailTitle = () => {
    if (!selectedItem) return 'Dettaglio';
    const s = selectedItem._source;
    if (s === 'openai') return 'Dettaglio Trascrizione';
    if (s === 'elevenlabs') return 'Conversazione ElevenLabs';
    if (s === 'gemini-voice') return 'Sessione Gemini Voice';
    return 'Conversazione Chatbot';
  };

  const getDetailIcon = () => {
    if (!selectedItem) return Phone;
    const s = selectedItem._source;
    if (s === 'openai') return Phone;
    if (s === 'elevenlabs') return Mic;
    if (s === 'gemini-voice') return Mic;
    return Bot;
  };

  const DetailIcon = getDetailIcon();

  return (
    <div className="space-y-6">
      {renderSourceTabs()}
      {renderStats()}
      {renderFilters()}
      {renderList()}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-foreground">
              <DetailIcon className="w-5 h-5" />
              {getDetailTitle()}
              {selectedItem?.sentiment_label &&
                getSentimentBadge(selectedItem.sentiment_label, selectedItem.sentiment_score)}
            </DialogTitle>
          </DialogHeader>

          {selectedItem && (
            <>
              {selectedItem._source === 'openai' && renderOpenAIDetail()}
              {selectedItem._source === 'elevenlabs' && renderElevenLabsDetail()}
              {selectedItem._source === 'gemini-voice' && renderGeminiVoiceDetail()}
              {selectedItem._source === 'chatbot' && renderChatbotDetail()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
